import { useMemo, useRef, useState, type ReactNode } from "react";
import { format } from "date-fns";
import { Link } from "react-router";
import {
  IconArrowLeft,
  IconCopy,
  IconDownload,
  IconExternalLink,
  IconLink,
  IconPlayerPlayFilled,
} from "@tabler/icons-react";
import type { DfyDeliveryDto, DfyOrderDto } from "@repo/api/dfy";
import { DFY_PACKAGE_LABELS } from "@repo/api/utils/dfy-billing";
import { getDfyOrderName } from "@repo/api/utils/dfy-order";
import type { SourceImageDto } from "@repo/api/source-image";
import type { AssetMediaDto } from "@repo/api/asset-media";
import { DfyApi } from "@/lib/api/dfy-api";
import {
  copyToClipboard,
  downloadFile,
  formatAudioDuration,
  formatCreatedAt,
} from "@/lib/utils";
import ImageBadge from "@/lib/components/ImageBadge";
import Toast from "@/lib/components/Toast";
import Tabs from "@/lib/components/Tabs";
import { Badge } from "@/lib/components/Badge";
import DfyOrderStatusBadge from "./DfyOrderStatusBadge";
import { Button } from "@/lib/components/Button/Button";
import Image from "@/lib/components/Image";
import { getQualityLabel } from "@/features/Editor/Navbar/ExportVideoDialog/EarlierExportsView";
import OrderDetailsSummary, {
  SummaryRow,
} from "./OrderForm/components/OrderDetailsSummary";
import CostBreakdown from "./OrderForm/components/CostBreakdown";
import DfyOrderBrandingSummary from "./DfyOrderBrandingSummary";
import {
  getDfyOrderImageItems,
  getDfySelectionNumbers,
} from "@/lib/stores/dfy-order-store/dfy-order-selectors";
import { ImageOrderBadge } from "./OrderForm/images/ImageMarkers";

export type DfyOrderDetailsTab = "deliveries" | "summary" | "images";

export default function DfyOrderDetailsLayout({
  teamExternalId,
  order,
  submittedByEmail,
  sourceImages,
  assetMedias,
  backTo,
  actions,
  deliveriesTab,
  tab,
  onTabChange,
  children,
}: {
  teamExternalId: string;
  order: DfyOrderDto;
  submittedByEmail: string | null;
  sourceImages: SourceImageDto[];
  assetMedias: AssetMediaDto[];
  backTo: string;
  actions?: ReactNode;
  deliveriesTab: ReactNode;
  tab: DfyOrderDetailsTab;
  onTabChange: (tab: DfyOrderDetailsTab) => void;
  children?: ReactNode;
}) {
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      <header className="flex shrink-0 flex-col gap-5 px-6 pt-6 pb-2">
        <Link
          to={backTo}
          className="flex w-fit items-center gap-2 text-sm text-text-secondary hover:text-text-primary"
        >
          <IconArrowLeft size={16} />
          Orders
        </Link>
        <div className="grid grid-cols-1 items-center gap-x-4 gap-y-2 md:grid-cols-[minmax(0,1fr)_auto]">
          <div className="min-w-0 md:col-start-1 md:row-start-1">
            <div className="flex min-w-0 flex-wrap items-center gap-3 gap-y-1.5">
              <h1
                className="min-w-0 text-2xl font-semibold wrap-anywhere"
                title={`Order #${order.externalId}`}
              >
                {getDfyOrderName(order.externalId)}
              </h1>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-text-secondary md:col-span-2 md:row-start-2">
            <DfyOrderStatusBadge status={order.status} />
            <Badge
              variant="outline"
              className="inline-flex h-auto shrink-0 gap-2 rounded-full px-3 py-1"
            >
              {DFY_PACKAGE_LABELS[order.details.type]} package
            </Badge>
          </div>
          {actions && (
            <div className="mt-2 flex flex-wrap items-center gap-2 md:col-start-2 md:row-start-1 md:mt-0 md:justify-self-end">
              {actions}
            </div>
          )}
        </div>
      </header>

      <div className="min-h-0 flex-1">
        <Tabs
          currentTab={tab}
          onTabClicked={onTabChange}
          options={[
            { label: "Deliveries", value: "deliveries" },
            { label: "Summary", value: "summary" },
            { label: "Images", value: "images" },
          ]}
          renderCurrentTab={() => (
            <div className="h-full overflow-x-hidden overflow-y-auto p-6">
              {tab === "deliveries" && deliveriesTab}
              {tab === "summary" && (
                <DfyOrderSummaryTab
                  teamExternalId={teamExternalId}
                  order={order}
                  submittedByEmail={submittedByEmail}
                  assetMedias={assetMedias}
                />
              )}
              {tab === "images" && (
                <DfyOrderImagesTab
                  teamExternalId={teamExternalId}
                  order={order}
                  sourceImages={sourceImages}
                />
              )}
            </div>
          )}
        />
      </div>

      {children}
    </div>
  );
}

function DfyOrderImagesTab({
  teamExternalId,
  order,
  sourceImages,
}: {
  teamExternalId: string;
  order: DfyOrderDto;
  sourceImages: SourceImageDto[];
}) {
  const { details } = order;
  const items = useMemo(
    () =>
      getDfyOrderImageItems(
        details,
        Object.fromEntries(
          sourceImages.map((image) => [image.externalId, image]),
        ),
      ),
    [details, sourceImages],
  );

  const handleDownload = async (sourceImageExternalId: string) => {
    try {
      const { downloadUrl } = await DfyApi.getSourceImageDownloadUrl(
        teamExternalId,
        order.externalId,
        sourceImageExternalId,
      );
      downloadFile(downloadUrl);
    } catch (error) {
      console.error(error);
      Toast.error("Failed to download image");
    }
  };

  if (items.length === 0) {
    const listingUrl = details.listingUrl.trim();
    if (!listingUrl) {
      return <p className="text-sm text-text-secondary">No images uploaded.</p>;
    }

    return (
      <div className="flex w-full max-w-154 items-center gap-3 rounded-xl border p-3">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-md">
          <IconLink className="h-5 w-5" color="url(#icon-gradient)" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-sm font-medium">Images from URL</span>
          <span
            className="truncate text-sm text-text-secondary"
            title={listingUrl}
          >
            {listingUrl}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0"
            aria-label="Copy listing link"
            onClick={() => copyToClipboard(listingUrl, "Link copied")}
          >
            <IconCopy size={16} />
          </Button>
          <Button asChild variant="ghost" size="icon" className="shrink-0">
            <a
              href={listingUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open listing"
            >
              <IconExternalLink size={16} />
            </a>
          </Button>
        </div>
      </div>
    );
  }

  const isUserSelection = details.imageSelectionMode === "USER";
  const selectionNumbers = getDfySelectionNumbers(items);

  return (
    <div className="flex flex-col gap-8">
      <div className="flex w-full max-w-154 flex-col gap-2 rounded-xl border p-4 text-sm">
        <SummaryRow
          label="Images and order chosen by"
          value={isUserSelection ? "Customer" : "Amplifiles editors"}
        />
      </div>
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
        {items.map(({ config, image }) => (
          <div key={image.externalId} className="group relative aspect-square">
            <Image
              src={image.thumbnailSrc}
              alt=""
              className="h-full w-full rounded-lg object-cover"
            />
            {isUserSelection && (
              <ImageOrderBadge
                orderNumber={selectionNumbers.get(config.externalId) ?? null}
                isSelected={config.isSelected}
              />
            )}
            <Button
              variant="ghost"
              size="iconSm"
              className="absolute right-2 bottom-2 bg-black/50 text-white opacity-0 transition-opacity group-hover:opacity-100 hover:bg-black/70 hover:text-white focus-visible:opacity-100"
              aria-label="Download image"
              onClick={() => handleDownload(image.externalId)}
            >
              <IconDownload size={18} />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function DfyOrderSummaryTab({
  teamExternalId,
  order,
  submittedByEmail,
  assetMedias,
}: {
  teamExternalId: string;
  order: DfyOrderDto;
  submittedByEmail: string | null;
  assetMedias: AssetMediaDto[];
}) {
  const { details, paymentDetails } = order;

  return (
    <div className="flex w-full max-w-154 flex-col gap-8">
      <OrderDetailsSummary details={details} />
      {paymentDetails && (
        <CostBreakdown
          lineItems={paymentDetails.lineItems}
          coupon={paymentDetails.coupon}
          creditsFromBalance={paymentDetails.creditsFromBalance}
          total={paymentDetails.stripeCredits}
        />
      )}
      <DfyOrderBrandingSummary
        teamExternalId={teamExternalId}
        orderExternalId={order.externalId}
        details={details}
        assetMedias={assetMedias}
      />
      {details.freeText.trim() && (
        <div className="flex flex-col gap-3">
          <p className="font-medium">Notes for the editors</p>
          <p className="rounded-xl border p-4 text-sm wrap-anywhere whitespace-pre-wrap text-text-secondary">
            {details.freeText}
          </p>
        </div>
      )}
      <div className="flex flex-col gap-3">
        <p className="font-medium">Order metadata</p>
        <div className="flex flex-col gap-2 rounded-xl border p-4 text-sm">
          <SummaryRow label="Submitted by" value={submittedByEmail ?? "-"} />
          <SummaryRow
            label="Submitted at"
            value={order.submittedAt ? format(order.submittedAt, "PPp") : "-"}
          />
          <SummaryRow label="Order ID" value={order.externalId} />
        </div>
      </div>
    </div>
  );
}

export function DfyDeliveryCard({
  delivery,
  badges,
  actions,
  footer,
  onDownload,
}: {
  delivery: DfyDeliveryDto;
  badges?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  onDownload?: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [durationMs, setDurationMs] = useState<number | null>(null);
  const {
    downloadUrl: src,
    aspectRatio,
    upscaleQuality,
  } = delivery.videoRender;

  return (
    <div>
      <div className="relative aspect-video">
        <video
          ref={videoRef}
          src={src ? `${src}#t=0.1` : undefined}
          preload="metadata"
          controls={hasStarted}
          playsInline
          onLoadedMetadata={(event) => {
            const { duration } = event.currentTarget;
            if (Number.isFinite(duration)) {
              setDurationMs(duration * 1000);
            }
          }}
          className="absolute inset-0 h-full w-full rounded-lg bg-black object-contain"
        />
        {!hasStarted && src && (
          <button
            type="button"
            onClick={() => {
              setHasStarted(true);
              videoRef.current?.play().catch(() => {});
            }}
            aria-label="Play video"
            className="group absolute inset-0 flex cursor-pointer items-center justify-center rounded-lg"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-black/50 text-white transition-colors group-hover:bg-black/70">
              <IconPlayerPlayFilled size={24} />
            </span>
          </button>
        )}
        {!hasStarted && durationMs !== null && (
          <ImageBadge className="right-3 bottom-2.5 rounded">
            {formatAudioDuration(durationMs)}
          </ImageBadge>
        )}
      </div>
      <div className="flex items-center gap-3 pt-2">
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex min-w-0 items-center gap-2">
            <p className="truncate font-semibold" title={delivery.name}>
              {delivery.name}
            </p>
            {aspectRatio && (
              <Badge size="xs" variant="outline" className="shrink-0">
                {aspectRatio}
              </Badge>
            )}
            {upscaleQuality === "4k" && (
              <Badge size="xs" variant="outline" className="shrink-0">
                {getQualityLabel(upscaleQuality)}
              </Badge>
            )}
            {badges}
          </div>
          <p className="truncate text-sm text-text-secondary">
            {formatCreatedAt(delivery.createdAt, "Delivered")}
          </p>
        </div>
        {src && (
          <Button
            asChild
            variant="outline"
            size="iconXs"
            className="shrink-0"
            aria-label="Download video"
          >
            <a href={src} download onClick={onDownload}>
              <IconDownload size={18} />
            </a>
          </Button>
        )}
        {actions}
      </div>
      {footer}
    </div>
  );
}
